import { Close, History } from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import {
  approveSGRWarrantyRequest,
  deleteSGRWarrantyRequestApproval,
  getSGRWarrantyRequestMissingApprovals,
  rejectSGRWarrantyRequest,
  updateSGRWarrantyRequestComitee,
} from 'lib/api/sgr';
import EnhancedTab, { TabRow } from 'lib/designSystem/TabSimulation';
import { showEntityRecord } from 'lib/functions/utils';
import { Warranty } from 'lib/types/models';
import Comitee from 'pages/Finance/Components/Financing/common/Comittee';
import { CommitteeService } from 'pages/Finance/Components/Financing/common/Comittee/types';
import RiskReviews from 'pages/Finance/Components/Financing/common/Risk';
import React, { useEffect } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import EditableSummary from './EditableSummary';
import File from './File';
import Payments from './Payments';
import Summary from './Summary/index';

const createSGRWarrantyCommitteeService = (
  warranty: Warranty,
  refreshWarranty?: () => void,
): CommitteeService => ({
  requestId: warranty.id,
  canEdit: warranty.status === 'IN_REVIEW' || warranty.status === 'REJECTED',
  isRejected: warranty.status === 'REJECTED',
  isActive: warranty.status === 'ACTIVE',
  getMissingApprovals: async (dispatch, requestId) => {
    const response = await getSGRWarrantyRequestMissingApprovals(
      dispatch,
      requestId,
    );
    return response;
  },
  approve: async (dispatch, requestId, data) => {
    await approveSGRWarrantyRequest(dispatch, requestId, data);
    if (refreshWarranty) refreshWarranty();
  },
  reject: async (dispatch, requestId, data) => {
    await rejectSGRWarrantyRequest(dispatch, requestId, data);
    if (refreshWarranty) refreshWarranty();
  },
  updateCommittee: async (dispatch, requestId, data) => {
    await updateSGRWarrantyRequestComitee(dispatch, requestId, data);
    if (refreshWarranty) refreshWarranty();
  },
  deleteApproval: async (dispatch, requestId, approvalId) => {
    await deleteSGRWarrantyRequestApproval(dispatch, requestId, approvalId);
    if (refreshWarranty) refreshWarranty();
  },
});

interface WarrantyDetailDialogProps {
  open: boolean;
  onClose: () => void;
  warranty: Warranty | null;
  refreshWarranty: () => void;
}

const WarrantyDetailDialog: React.FC<WarrantyDetailDialogProps> = ({
  open,
  onClose,
  warranty,
  refreshWarranty,
}) => {
  if (!warranty) return null;
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);

  const committeeService = createSGRWarrantyCommitteeService(
    warranty,
    refreshWarranty,
  );

  const showPayments =
    warranty.status === 'ACTIVE' || warranty.status === 'APPROVED';

  const handleOpenEntityRecord = () => {
    showEntityRecord(dispatch, {
      type: 'SGRWarrantyRequest',
      id: warranty.id,
    });
  };

  const tabs = [
    {
      label: 'Resumen',
      component: isEditing ? (
        <EditableSummary
          warranty={warranty}
          setIsEditing={setIsEditing}
          refreshWarranty={refreshWarranty}
        />
      ) : (
        <Summary
          warranty={warranty}
          setIsEditing={setIsEditing}
          refreshWarranty={refreshWarranty}
        />
      ),
    },
    showPayments && {
      label: 'Pagos',
      component: <Payments warrantyId={warranty.id} />,
    },
    {
      label: 'Aprobación',
      component: <Comitee committeeService={committeeService} />,
    },
    {
      label: 'Riesgo',
      component: (
        <RiskReviews item={warranty} requestTypes={['sgr-warranty-request']} />
      ),
    },
    {
      label: 'Documentos',
      component: <File warranty={warranty} />,
    },
  ].filter(Boolean) as TabRow[];

  useEffect(() => {
    if (!open) setIsEditing(false);
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isEditing ? 'lg' : 'md'}
      fullWidth
    >
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: 'absolute', top: 10, right: 10 }}
      >
        <Close />
      </IconButton>
      <div style={{ padding: '0 20px' }}>
        <EnhancedTab
          title={`Garantía #${warranty.id}`}
          tabs={tabs}
          record={
            warranty.id && (
              <IconButton
                onClick={handleOpenEntityRecord}
                size="small"
                style={{ marginRight: 2 }}
                title="Ver historial de la entidad"
              >
                <History />
              </IconButton>
            )
          }
        />
      </div>
    </Dialog>
  );
};

export default WarrantyDetailDialog;
